/* globals document */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';
import styled from 'styled-components';

import Button from '../../common/ui/Button';
import Close from '../../icons/close.svg';
import Menu from '../../icons/menu.svg';

import MobileLink from './MobileLink';
import MobileLinkGroup from './MobileLinkGroup';
import MobileLogo from './MobileLogo';

const MobileMenu = (props) => {
  const [isOpen, setOpen] = useState(false);
  const [isBrowser, setIsBrowser] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'visible';
  }, [isOpen]);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const openMenu = () => {
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
  };

  const { mode } = props;

  return (
    <>
      <MenuToggle title="Open navigation." onClick={openMenu}>
        <StyledMenu mode={mode} />
      </MenuToggle>
      {isBrowser &&
        createPortal(
          <FocusLock disabled={!isOpen}>
            <MobileNavigation isOpen={isOpen}>
              <Header>
                <MobileLogo />
                <CloseButton onClick={closeMenu} title="Close navigation.">
                  <StyledClose />
                </CloseButton>
              </Header>
              <Links>
                <MobileLinkGroup label="Solutions">
                  <MobileLink to="/solutions" label="Overview" />
                  <MobileLink to="/solutions/pos" label="Point of Sale" />
                  <MobileLink
                    to="/solutions/club"
                    label="Clubs &amp; Subscriptions"
                  />
                  <MobileLink to="/solutions/ecommerce" label="Ecommerce" />
                  <MobileLink to="/solutions/crm" label="CRM" />
                  <MobileLink
                    to="/solutions/reservations"
                    label="Reservations"
                  />
                  <MobileLink to="/solutions/payments" label="Payments" />
                </MobileLinkGroup>
                <MobileLinkGroup label="Customers">
                  <MobileLink to="/customers" label="Overview" />
                  <MobileLink
                    to="/customers/united-states/pacific-northwest"
                    label="United States"
                  />
                  <MobileLink to="/customers/canada/niagara" label="Canada" />
                  <MobileLink
                    to="/customers/australia-and-new-zealand/australia"
                    label="Australia / New Zealand"
                  />
                  <MobileLink
                    to="/customers/south-africa"
                    label="South Africa"
                  />
                  <MobileLink to="/customers/europe" label="Europe" />
                  <MobileLink to="/customers/small" label="Small" />
                  <MobileLink to="/customers/midsize" label="Midsize" />
                  <MobileLink to="/customers/enterprise" label="Enterprise" />
                </MobileLinkGroup>
                <MobileLinkGroup label="Partners">
                  <MobileLink to="/partners" label="Overview" />
                  <MobileLink to="/partners/apps" label="App Store" />
                  <MobileLink
                    to="/partners/designers"
                    label="Find a Design Partner"
                  />
                  <MobileLink
                    to="/partners/become-a-partner"
                    label="Become a Partner"
                  />
                  <MobileLink
                    href="https://design-docs.commerce7.com/docs"
                    label="Designer Docs"
                  />
                  <MobileLink
                    href="https://developer.commerce7.com/docs"
                    label="Developer Docs"
                  />
                </MobileLinkGroup>
                <MobileLinkGroup label="Pricing">
                  <MobileLink to="/pricing" label="Plans &amp; Pricing" />
                  <MobileLink to="/pricing/templates" label="Templates" />
                </MobileLinkGroup>
                <MobileLinkGroup label="Resources">
                  <MobileLink to="/resources" label="Overview" />
                  <MobileLink to="/blog" label="Blog" />
                  <MobileLink to="/article" label="Articles" />
                  <MobileLink
                    href="https://documentation.commerce7.com"
                    label="Documentation"
                  />
                  <MobileLink
                    href="https://commerce7academy.commerce7.com/"
                    label="Commerce7 Academy"
                  />
                  <MobileLink
                    href="https://vimeo.com/showcase/c7videohub"
                    label="Video Hub"
                  />
                </MobileLinkGroup>
                <MobileLinkGroup label="Contact Us" to="/contact" />
              </Links>
              <Actions>
                <LoginButton
                  variant="white"
                  href="https://admin.commerce7.com/login"
                >
                  Login
                </LoginButton>
                <ScheduleDemoButton to="/demo">Book a Demo</ScheduleDemoButton>
              </Actions>
            </MobileNavigation>
          </FocusLock>,
          document.body
        )}
    </>
  );
};

const MenuToggle = styled.button`
  background: none;
  appearance: none;
  border: none;
  cursor: pointer;
  margin-left: 30px;
  display: flex;
  align-items: center;
  padding: 5px;
  ${({ theme }) => `
  @media ${theme.xLargeUp} {
    display: none;
  }
`}
`;

const StyledMenu = styled(Menu)`
  width: 22px;
  path {
    fill: ${({ mode }) => (mode === 'dark' ? '#ffffff' : 'rgb(10,17,44)')};
  }
`;

const StyledClose = styled(Close)`
  width: 22px;
`;

const CloseButton = styled.button`
  background: none;
  appearance: none;
  border: none;
  cursor: pointer;
  margin-left: 30px;
  display: flex;
  align-items: center;
  padding: 5px;
`;

const MobileNavigation = styled.nav`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  padding: 0 20px;
  background: rgb(11, 17, 36);
  transition: all 0.3s ease-in-out;
  overflow-y: auto;

  ${({ isOpen }) => `
    opacity: ${isOpen ? '1' : '0'};
    z-index: ${isOpen ? '301' : '-1'};
    pointer-events: ${isOpen ? 'initial' : 'none'};
  `}

  ${({ theme }) => `
    @media ${theme.xLargeUp} {
      display: none;
    }
    @media ${theme.mediumUp} {
      padding: 0 50px;
    }
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 116px;

  width: 100%;
`;

const Links = styled.div`
  padding-top: 30px;
  flex: auto;
  width: 100%;
  padding-bottom: 250px;

  ${({ theme }) => `
    @media ${theme.mediumUp} {
      padding-top: 80px;
    }
  `}
`;

const Actions = styled.div`
  display: block;
  width: 100%;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 30px 20px;
  background: rgb(11, 17, 36);
  border-top: 1px solid rgba(255, 255, 255, 0.2);

  ${({ theme }) => `
    @media ${theme.mediumUp} {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  `}
`;

const ScheduleDemoButton = styled(Button)`
  width: 100%;
  ${({ theme }) => `
    @media ${theme.mediumUp} {
      width: calc(50% - 10px);
    }
  `}
`;

const LoginButton = styled(Button)`
  margin-bottom: 20px;
  width: 100%;
  ${({ theme }) => `
    @media ${theme.mediumUp} {
      margin-bottom: 0px;
      width: calc(50% - 10px);
    }
`}
`;

export default MobileMenu;
